/**
 * WindowLauncher — floating button + popover menu for toggling windows.
 *
 * Generalized from the kit's old `ui/debug/DebugLauncher.tsx` (Phase 1 of the
 * UI-framework arc). Bottom-right region of the viewport, but immediately LEFT
 * of the player-facing quick-menu ≡ launcher: the right inset is derived from
 * the shared HUD_PANEL_DEFAULTS.quickMenu values plus the documented button
 * sizes (N80, 2026-09-28 — see the clearance block below). Low-key visual
 * weight (small wrench emoji button). Click opens a popover listing every
 * window from WindowManagerContext with a toggle checkbox, icon, and one-line
 * description.
 *
 * Active-count badge on the button (count of currently-open windows).
 *
 * Keyboard:
 *   Shift+D — in floating mode: open the hub-only surface. In hub mode:
 *   toggle the launcher panel.
 *   Shift+W — "WTF was that" 5s diagnostic-signal capture.
 *
 * Windows default to closed; opening one mounts its ToolWindow via the
 * sibling WindowHost. All test ids are preserved (`debug-launcher*`).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useWindows } from './WindowManagerContext';
import { CATEGORY_META, type WindowCategory } from './types';
import { isScreensaverMode } from '../../../config/screensaver/isScreensaverMode';
import { isMainGameDomain } from '../../../config/environment/environment';
import { storage } from '../../services/StorageService';
import { readFreezePopupEnabled, writeFreezePopupEnabled } from '../HUD/freezePopupPreference';
import { isDebugSurface } from '../../../shared/diagnostics/isDebugSurface';
import { HUD_PANEL_DEFAULTS } from '../../../config/ui/hudDefaults.config';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import './WindowLauncher.css';

const BUTTON_SIZE = 38;
const PANEL_WIDTH = 480; // widened again — labels were truncating at 340 (DJ 2026-07-09)

// ── Quick-menu clearance (N80, 2026-09-28) ─────────────────────────────
// The 🛠 cluster stays in the bottom-right region, but sits LEFT of the
// player-facing quick-menu ≡ launcher. Its right inset is DERIVED from the
// shared HUD defaults + the documented button sizes so the two zones cannot
// drift into each other again (N77/A05: the old fixed `right: 12`, 38 px
// button sat on top of the ≡ button at `right: 24` and swallowed every click
// aimed at it):
//
//   HUD_PANEL_DEFAULTS.quickMenu.position.offset (24)  ≡ launcher right inset
// + QUICK_MENU_LAUNCHER_SIZE                      (34)  ≡ minWidth/height
// + DEBUG_LAUNCHER_QUICK_MENU_GAP                 (8)   breathing room
// = DEBUG_LAUNCHER_RIGHT_INSET                    (66)  🛠 wrapper right inset
//
// The badge overhangs the button by 4 px, so the 8 px gap keeps even the badge
// clear of the ≡ zone. The freeze-popup and QA-mob widgets hang further LEFT of
// the button, away from the ≡ zone. Verified by WindowLauncher.overlap.test.tsx.
//
// The same suite also guards the EXPANDED quick-menu panel: its rows stretch
// the panel width, so `locator.click()` on a row aims at the row-centre column
// (quickMenu offset + panel width / 2 = 122 px from the right edge). The
// freeze-popup widget is wide (minWidth 142) and hangs left of the 🛠 button,
// so its right edge must stay LEFT of that column or it swallows the row click
// (N80: A05 opens Settings through this menu).
/** Collapsed ≡ launcher size — QuickMenu.tsx `launcherStyle` minWidth/height. */
export const QUICK_MENU_LAUNCHER_SIZE = 34;
/** Positive gap between the 🛠 interactive zone and the ≡ interactive zone. */
export const DEBUG_LAUNCHER_QUICK_MENU_GAP = 8;
/** Badge `right: -4` overhang that the documented gap must absorb. */
export const DEBUG_LAUNCHER_BADGE_OVERHANG = 4;
export const DEBUG_LAUNCHER_RIGHT_INSET =
  HUD_PANEL_DEFAULTS.quickMenu.position.offset
  + QUICK_MENU_LAUNCHER_SIZE
  + DEBUG_LAUNCHER_QUICK_MENU_GAP;
export const DEBUG_LAUNCHER_BOTTOM_INSET = 12;
export const DEBUG_LAUNCHER_BUTTON_SIZE = BUTTON_SIZE;
/** QuickMenu.tsx `shellStyle` — the expanded panel's border-box width. */
export const QUICK_MENU_PANEL_WIDTH = 196;
/** Horizontal centre of an expanded quick-menu row — the pointer click point
 *  (the panel is symmetric, so it is the quickMenu offset + width / 2). */
export const QUICK_MENU_ROW_CENTER_INSET =
  HUD_PANEL_DEFAULTS.quickMenu.position.offset
  + QUICK_MENU_PANEL_WIDTH / 2;
/** Margin the freeze widget keeps LEFT of that row-centre column. */
export const DEBUG_FREEZE_WIDGET_ROW_CLEARANCE = 12;
/** Freeze-popup widget `right`, relative to the 🛠 wrapper. */
export const DEBUG_FREEZE_WIDGET_RIGHT =
  QUICK_MENU_ROW_CENTER_INSET
  + DEBUG_FREEZE_WIDGET_ROW_CLEARANCE
  - DEBUG_LAUNCHER_RIGHT_INSET;
/** Freeze-popup widget minWidth (its inline style). */
export const DEBUG_FREEZE_WIDGET_MIN_WIDTH = 142;
/** Gap between adjacent debug widgets. */
export const DEBUG_WIDGET_GAP = 8;
/** QA-mob widget `right`, relative to the 🛠 wrapper (left of the freeze chip). */
export const DEBUG_QA_WIDGET_RIGHT =
  DEBUG_FREEZE_WIDGET_RIGHT + DEBUG_FREEZE_WIDGET_MIN_WIDTH + DEBUG_WIDGET_GAP;
// The compact raise lives in WindowLauncher.css (chat-dock clearance). The
// overlap suite compares these against the stylesheet text so they cannot drift
// apart from the @media query.
export const DEBUG_LAUNCHER_COMPACT_MAX_WIDTH = 430;
export const DEBUG_LAUNCHER_COMPACT_MAX_HEIGHT = 520;
export const DEBUG_LAUNCHER_COMPACT_BOTTOM_INSET = 264;

// ── Debug Toolbox cluster member (U1, 2026-09-28) ──────────────────────
// DJ's U01 review: "Move top left icon tool to bottom right next to our
// other[s]." The collapsed Debug Toolbox chip (🛠️, 34 px) left the top-left
// HUD corner and now shares the 🛠 dev launcher's bottom-right column,
// directly ABOVE the button, so it clears every bottom-anchored chip:
//
//   right  = DEBUG_LAUNCHER_RIGHT_INSET                (66)
//   bottom = DEBUG_LAUNCHER_BOTTOM_INSET               (12)
//          + DEBUG_LAUNCHER_BUTTON_SIZE                (38)
//          + DEBUG_TOOLBOX_CLUSTER_GAP                 (8)
//          = DEBUG_TOOLBOX_BOTTOM_INSET                (58)
//
// The freeze-popup/QA chips are `bottom: 0` inside the 🛠 wrapper (top edge at
// 12 + 38 = 50 px), so the 8 px gap — wider than the badge's 4 px overhang —
// clears them at every viewport, including the compact raise (WindowLauncher
// .css lifts the whole wrapper; the chip keeps the base slot, so the
// separation only grows). The expanded Debug Toolbox panel grows UPWARD from
// the chip; DEBUG_TOOLBOX_PANEL_MAX_HEIGHT caps its top inside the viewport.
// Verified by DebugToolbox.placement.test.tsx.
/** Gap between the Debug Toolbox chip and the 🛠 button below it. */
export const DEBUG_TOOLBOX_CLUSTER_GAP = 8;
/** Collapsed Debug Toolbox chip right inset — same column as the 🛠 button. */
export const DEBUG_TOOLBOX_RIGHT_INSET = DEBUG_LAUNCHER_RIGHT_INSET;
/** Collapsed Debug Toolbox chip bottom inset — one gap above the 🛠 button. */
export const DEBUG_TOOLBOX_BOTTOM_INSET =
  DEBUG_LAUNCHER_BOTTOM_INSET + DEBUG_LAUNCHER_BUTTON_SIZE + DEBUG_TOOLBOX_CLUSTER_GAP;
/** Collapsed Debug Toolbox chip size — DebugToolbox.tsx collapsed button. */
export const DEBUG_TOOLBOX_BUTTON_SIZE = 34;
/** Margin the capped expanded Debug Toolbox panel keeps below the top edge. */
export const DEBUG_TOOLBOX_PANEL_TOP_MARGIN = 8;
/** CSS maxHeight of the expanded Debug Toolbox panel: `100vh` minus the chip
 *  anchor and the top margin, so its top never leaves the viewport. */
export const DEBUG_TOOLBOX_PANEL_MAX_HEIGHT =
  `calc(100vh - ${DEBUG_TOOLBOX_BOTTOM_INSET + DEBUG_TOOLBOX_PANEL_TOP_MARGIN}px)`;

interface ToolEntryProps {
  testId: string;
  open: boolean;
  icon: string;
  label: string;
  description: string;
  onToggle: () => void;
}

// Compact grid-cell toggle. Rows × columns layout (DJ 2026-05-29): icon + label
// tile, highlighted when open; the (long) description moves to the hover title
// so every cell stays uniform. testId/onToggle unchanged so the launcher tests
// + badge counting are layout-agnostic.
const ToolEntry: React.FC<ToolEntryProps> = ({ testId, open, icon, label, description, onToggle }) => (
  <button
    data-testid={testId}
    onClick={onToggle}
    title={`${label} — ${description}`}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 5,
      padding: '5px 6px',
      minWidth: 0,
      background: open ? 'rgba(80, 130, 200, 0.22)' : 'rgba(255, 255, 255, 0.03)',
      border: `1px solid ${open ? '#7af' : '#2a2f3a'}`,
      borderRadius: 4,
      color: open ? '#fff' : '#ccc',
      cursor: 'pointer',
      textAlign: 'left',
      fontFamily: 'inherit',
      fontSize: 11,
    }}
  >
    <span aria-hidden="true" style={{ flex: '0 0 auto', fontSize: 12, opacity: open ? 1 : 0.7 }}>
      {open ? '✓' : icon}
    </span>
    <span
      style={{
        flex: 1,
        minWidth: 0,
        fontWeight: open ? 600 : 400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  </button>
);

// Shared grid wrapper for the toggle tiles — auto-flows into as many columns as
// fit the panel width.
const toolGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
  gap: 4,
};

// Category section header — mirrors the hub sidebar's grouping so the
// launcher popover and the hub present windows in the same order.
const categoryHeaderStyle: React.CSSProperties = {
  margin: '6px 0 3px',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#8a93a6',
};

export const WindowLauncher: React.FC = () => {
  const { widgets, windows, toggle, closeAll, openAll, displayMode, setDisplayMode } = useWindows();
  const [panelOpen, setPanelOpen] = useState(false);
  const [freezePopupEnabled, setFreezePopupEnabled] = useState(readFreezePopupEnabled);
  const [qaMobStatus, setQaMobStatus] = useState('ready');
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const localQaMobTools = typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  const triggerQaMobAction = useCallback((action: 'wipe' | 'respawn') => {
    const type = action === 'wipe' ? 'debug_mapWipe' : 'debug_respawnMobs';
    EventBus.getInstance().emit(EVENTS.PLAYER_ACTION_INTENT, { type, payload: {} });
    setQaMobStatus(action === 'wipe' ? 'wipe fired' : 'mobs restored');

    const toast = (window as unknown as {
      __errorToast?: (level: 'error' | 'warn' | 'info', message: string) => void;
    }).__errorToast;
    toast?.('info', action === 'wipe'
      ? '💥 QA map wipe fired — Shift+G'
      : '♻️ Authored map mobs restored');
  }, []);

  const openCount = windows.reduce(
    (n, w) => n + (widgets[w.id]?.enabled ? 1 : 0),
    0,
  );
  const totalWindowCount = windows.length;
  // "Open all" opens only the dev windows (openAll excludes gameplay-kind),
  // so the close-all flip threshold is the dev-window count, not the total.
  const devWindowCount = windows.filter((w) => w.kind !== 'gameplay').length;

  // Click-outside closes panel.
  useEffect(() => {
    if (!panelOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        setPanelOpen(false);
      }
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [panelOpen]);

  useEffect(() => storage.subscribeUserPrefs(() => {
    setFreezePopupEnabled(readFreezePopupEnabled());
  }), []);

  // Shift+D: floating → clean hub-only surface; in hub mode it toggles the
  // launcher panel. Hub rows never create floating cards.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      // Dev keybinds (Shift+D launcher toggle, Shift+W WTF-capture) don't
      // belong on the public surface. The render bail above hides the button,
      // but this listener still registers (hooks run before the early return),
      // so gate the handler itself to avoid mutating window-manager state or
      // firing diagnostic captures on helix2000.com.
      if (isMainGameDomain()) return;

      // Local QA map wipe on Shift+G, a key no game binding uses (plan 29 lane BS;
      // it sat on Digit0 and swallowed hotbar slot 10). Capture phase: never reaches InputSystem.
      if (localQaMobTools && !e.repeat && e.shiftKey && e.code === 'KeyG'
        && !e.metaKey && !e.ctrlKey && !e.altKey) {
        triggerQaMobAction('wipe');
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      if (e.shiftKey && (e.key === 'D' || e.key === 'd') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (displayMode.kind !== 'hub') {
          setDisplayMode('hub');
        } else {
          setPanelOpen((p) => !p);
        }
        e.preventDefault();
        return;
      }

      // Shift+W — "WTF was that" capture. Snaps the last 5 seconds of all
      // diagnostic signals (reconciliation events, frame jitter, browser
      // perf) into a markdown digest, copies to clipboard, logs to console.
      if (e.shiftKey && (e.key === 'W' || e.key === 'w') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        import('../../diagnostics/WtfCapture').then((m) => {
          m.captureMoment().then((r) => {
            const msg = r.snapCount > 0
              ? `📸 Captured · ${r.snapCount} snap(s) · worst ${r.worstDivergencePx.toFixed(0)}px${r.copied ? ' · copied' : ''}`
              : `📸 Captured · no snaps · max div ${r.worstDivergencePx.toFixed(0)}px${r.copied ? ' · copied' : ''}`;
            const toast = (window as unknown as { __errorToast?: (level: 'error' | 'warn' | 'info', m: string) => void }).__errorToast;
            if (toast) toast('info', msg);
          });
        });
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [displayMode.kind, localQaMobTools, setDisplayMode, triggerQaMobAction]);

  const handleToggleAll = useCallback(() => {
    if (openCount >= devWindowCount) closeAll();
    else openAll();
  }, [openCount, devWindowCount, closeAll, openAll]);

  const kind = displayMode.kind;
  const toggleFreezePopups = useCallback(() => {
    const next = !freezePopupEnabled;
    setFreezePopupEnabled(next);
    writeFreezePopupEnabled(next);
  }, [freezePopupEnabled]);

  // Screensaver mode = clean stream viewport — hide the 🛠 launcher button
  // (the small bottom-right dev cluster that survived GameUIOverlay's
  // isScreensaverMode() early-return because WindowLauncher is mounted
  // separately in main.tsx, outside the GameUIOverlay tree). Placed AFTER all
  // hooks so the rules-of-hooks hold.
  //
  // Main game domain (helix2000.com) = public surface — the 🛠 launcher is a
  // dev/QA affordance that lists every diagnostic window (latency, netTraffic,
  // tickBudget, frame jitter, …) with an "open all" button. Public players
  // should never see it. Mirrors the __helixDebug hostname gate. Placed AFTER
  // all hooks so the rules-of-hooks hold.
  if (isScreensaverMode() || isMainGameDomain()) return null;

  return (
    <div
      ref={wrapperRef}
      data-testid="debug-launcher"
      style={{
        position: 'fixed',
        // Derived from HUD_PANEL_DEFAULTS.quickMenu — see the clearance block
        // above; must clear the ≡ launcher at every viewport (N80).
        right: DEBUG_LAUNCHER_RIGHT_INSET,
        bottom: DEBUG_LAUNCHER_BOTTOM_INSET,
        zIndex: 99999,
        pointerEvents: 'auto',
        fontFamily: 'monospace',
      }}
    >
      <button
        data-testid="debug-launcher-button"
        aria-label="Open debug tools launcher"
        title="Debug tools (Shift+D)"
        onClick={() => setPanelOpen((p) => !p)}
        style={{
          width: BUTTON_SIZE,
          height: BUTTON_SIZE,
          borderRadius: BUTTON_SIZE / 2,
          background: panelOpen ? 'rgba(80, 130, 200, 0.95)' : 'rgba(20, 24, 36, 0.85)',
          border: '1px solid #3a4456',
          color: '#fff',
          fontSize: 18,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        }}
      >
        🛠
        {openCount > 0 && (
          <span
            data-testid="debug-launcher-badge"
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 16,
              height: 16,
              borderRadius: 8,
              padding: '0 4px',
              background: '#ff7f3f',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #1a1a1a',
            }}
          >
            {openCount}
          </span>
        )}
      </button>

      {/* Freeze-popup widget — HIDDEN while freeze popups are off (2026-07-26).
          BIG-FREEZE-01 closed: the ~11s metronome was a GNOME Shell extension
          blocking Mutter's main loop, not the game, so a permanent freeze
          toggle no longer earns screen space. Nothing is deleted — the widget
          returns automatically the moment popups are re-enabled (Settings ->
          Interface, or ?freezePopup=1), and it always shows on debug surfaces.
          Detection and durable DomainLog recording continue regardless. */}
      {(freezePopupEnabled || isDebugSurface()) && (
      <button
        type="button"
        data-testid="debug-launcher-freeze-popup-widget"
        aria-label="Toggle freeze detail popups"
        aria-pressed={freezePopupEnabled}
        onClick={toggleFreezePopups}
        title="Persistent freeze detail cards. Detection, logging, and the brief corner tag remain active when OFF."
        style={{
          position: 'absolute',
          right: DEBUG_FREEZE_WIDGET_RIGHT,
          bottom: 0,
          height: BUTTON_SIZE,
          minWidth: DEBUG_FREEZE_WIDGET_MIN_WIDTH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '0 10px',
          borderRadius: 6,
          background: freezePopupEnabled ? 'rgba(64, 43, 24, 0.94)' : 'rgba(20, 24, 36, 0.92)',
          border: `1px solid ${freezePopupEnabled ? '#c47a39' : '#3a4456'}`,
          color: freezePopupEnabled ? '#ffd1a3' : '#a8afbd',
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          cursor: 'pointer',
          fontFamily: 'monospace',
          fontSize: 10,
          whiteSpace: 'nowrap',
        }}
      >
        <span>❄ freeze popups</span>
        <span style={{ fontWeight: 700, letterSpacing: '0.06em' }}>
          {freezePopupEnabled ? 'ON' : 'OFF'}
        </span>
      </button>
      )}

      {localQaMobTools && (
        <div
          data-testid="debug-launcher-qa-mob-widget"
          aria-label="Loot and quest mob QA controls"
          style={{
            position: 'absolute',
            right: DEBUG_QA_WIDGET_RIGHT,
            bottom: 0,
            height: BUTTON_SIZE,
            display: 'flex',
            alignItems: 'stretch',
            overflow: 'hidden',
            borderRadius: 6,
            background: 'rgba(27, 16, 24, 0.94)',
            border: '1px solid #9f4464',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
          }}
        >
          <button
            type="button"
            data-testid="debug-launcher-map-wipe"
            aria-label="Map wipe ability, keyboard shortcut Shift+G"
            title="Defeat every hostile mob through the real quest, XP, gold, loot, and respawn pipeline (Shift+G)"
            onClick={() => triggerQaMobAction('wipe')}
            style={{
              border: 0,
              borderRight: '1px solid #71364e',
              background: 'rgba(112, 25, 54, 0.7)',
              color: '#ffd0dc',
              padding: '0 10px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            💥 [⇧G] WIPE
          </button>
          <button
            type="button"
            data-testid="debug-launcher-respawn-mobs"
            aria-label="Respawn all authored map mobs"
            title="Immediately restore every authored mob spawn on this map"
            onClick={() => triggerQaMobAction('respawn')}
            style={{
              border: 0,
              background: 'transparent',
              color: '#ffc2a3',
              padding: '0 9px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            ♻ MOBS
          </button>
          <span
            aria-live="polite"
            style={{
              alignSelf: 'center',
              padding: '0 7px 0 2px',
              color: '#ad8492',
              fontSize: 8,
            }}
          >
            {qaMobStatus}
          </span>
        </div>
      )}

      {panelOpen && (
        <div
          data-testid="debug-launcher-panel"
          style={{
            position: 'absolute',
            right: 0,
            left: 'auto',
            bottom: BUTTON_SIZE + 8,
            width: `min(${PANEL_WIDTH}px, calc(100vw - 24px))`,
            // Reserve the raised launcher position and the panel's 46px anchor,
            // plus a 12px top margin. Border-box sizing includes its padding
            // and border in the available height; the tool list then scrolls.
            boxSizing: 'border-box',
            maxHeight: 'min(84vh, calc(100vh - var(--debug-launcher-clearance, 12px) - 58px))',
            overflowY: 'auto',
            background: 'rgba(12, 14, 22, 0.97)',
            border: '1px solid #3a4456',
            borderRadius: 6,
            padding: 8,
            color: '#ddd',
            fontSize: 12,
            boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '2px 4px 6px',
              borderBottom: '1px solid #2a2f3a',
              marginBottom: 4,
            }}
          >
            <span style={{ color: '#7af', letterSpacing: '0.08em', fontWeight: 600 }}>
              DEBUG TOOLS
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                data-testid="debug-launcher-mode-toggle"
                onClick={() => setDisplayMode(kind === 'hub' ? 'floating' : 'hub')}
                style={{
                  background: kind === 'hub' ? 'rgba(80, 130, 200, 0.30)' : 'transparent',
                  color: kind === 'hub' ? '#fff' : '#aaa',
                  border: `1px solid ${kind === 'hub' ? '#7af' : '#2a2f3a'}`,
                  borderRadius: 3,
                  padding: '2px 6px',
                  fontSize: 10,
                  cursor: 'pointer',
                  fontFamily: 'monospace',
                  fontWeight: kind === 'hub' ? 600 : 400,
                }}
                title={kind === 'hub'
                  ? 'Currently: 📊 HUB mode — only the hub container renders, no floating windows. Click to switch to 🔲 floating.'
                  : 'Currently: 🔲 floating mode — each tool gets its own draggable window. Click to switch to 📊 hub only.'}
              >
                {kind === 'hub' ? '📊 hub only' : '🔲 floating'}
              </button>
              <button
                data-testid="debug-launcher-toggle-all"
                onClick={handleToggleAll}
                style={{
                  background: 'transparent',
                  color: '#aaa',
                  border: '1px solid #2a2f3a',
                  borderRadius: 3,
                  padding: '2px 6px',
                  fontSize: 10,
                  cursor: 'pointer',
                  fontFamily: 'monospace',
                }}
                title={openCount >= devWindowCount ? 'Close all windows' : 'Open all dev tools (gameplay windows excluded)'}
              >
                {openCount >= devWindowCount ? 'close all' : 'open all'}
              </button>
            </div>
          </div>

          <button
            type="button"
            data-testid="debug-launcher-freeze-popup-toggle"
            aria-pressed={freezePopupEnabled}
            onClick={toggleFreezePopups}
            title="Show or hide persistent client-freeze detail cards. Freeze detection, logging, and the brief corner tag remain active."
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '7px 8px',
              marginBottom: 5,
              background: freezePopupEnabled ? 'rgba(255, 160, 64, 0.13)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${freezePopupEnabled ? '#c47a39' : '#2a2f3a'}`,
              borderRadius: 4,
              color: freezePopupEnabled ? '#ffd1a3' : '#a8afbd',
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontSize: 11,
              textAlign: 'left',
            }}
          >
            <span>❄ freeze detail popups</span>
            <span style={{ fontWeight: 700, letterSpacing: '0.06em' }}>
              {freezePopupEnabled ? 'ON' : 'OFF'}
            </span>
          </button>

          {/* Grouped by category — same derived order as the hub sidebar
              (CATEGORY_META insertion order; DebugSuiteContainer pattern).
              Was a flat 53-tile grid. TestIds unchanged (layout-agnostic). */}
          {(Object.keys(CATEGORY_META) as WindowCategory[]).map((cat) => {
            const defs = windows.filter((w) => w.category === cat);
            if (defs.length === 0) return null;
            return (
              <div key={cat}>
                <div style={categoryHeaderStyle}>
                  {CATEGORY_META[cat].icon} {CATEGORY_META[cat].label}
                </div>
                <div style={toolGridStyle}>
                  {defs.map((def) => {
                    const open = widgets[def.id]?.enabled ?? false;
                    return (
                      <ToolEntry
                        key={def.id}
                        testId={`debug-launcher-toggle-${def.id}`}
                        open={open}
                        icon={def.icon}
                        label={def.label}
                        description={def.description}
                        onToggle={() => toggle(def.id)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div
            style={{
              marginTop: 4,
              paddingTop: 4,
              borderTop: '1px solid #2a2f3a',
              fontSize: 10,
              color: '#666',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>Shift+D</span>
            <span>{openCount} / {totalWindowCount} open</span>
          </div>
        </div>
      )}
    </div>
  );
};
